import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.#loadDeferredMenu().then(() => {
      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => this.#initSwipers(details), 0);
        if (this.classList.contains('header-drawer--mobile')) {
          document.documentElement.setAttribute('scroll-lock', '');
        }
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        if (this.classList.contains('header-drawer--mobile')) {
          document.documentElement.removeAttribute('scroll-lock');
        }
        removeTrapFocus();
        this.#setRootScreenActive();
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

    #setRootScreenActive() {
    const container = this.querySelector('.drawer-push-menu');

    if (!container) return;

    const screens = container.querySelectorAll('.dpm-screen');
    screens.forEach((screen) => {
      screen.classList.remove('is-active');
    });

    const root = container.querySelector('[data-screen="root"]');
    if (root) {
      root.classList.add('is-active');
    }

    container.removeAttribute('data-stack');

    const scrollContainer =
      this.querySelector('.menu-drawer__container') ||
      this.querySelector('.menu-drawer') ||
      container;

    scrollContainer.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #loadDeferredMenu() {
    const container = this.querySelector('[data-deferred-menu-container]');
    if (!container || container.dataset.deferredMenuLoaded === 'true') return Promise.resolve();

    const template = this.querySelector('template[data-deferred-menu-template]');

    if (template instanceof HTMLTemplateElement) {
      container.append(template.content.cloneNode(true));
      template.remove();
      container.dataset.deferredMenuLoaded = 'true';
      this.#setupAnimatedElementListeners();
      return Promise.resolve();
    }

    if (!this.hasAttribute('data-deferred-menu-remote')) return Promise.resolve();
    if (this.__deferredMenuPromise) return this.__deferredMenuPromise;

    this.__deferredMenuPromise = this.#fetchDeferredMenu(container).finally(() => {
      this.__deferredMenuPromise = null;
    });

    return this.__deferredMenuPromise;
  }

  async #fetchDeferredMenu(container) {
    const sectionId = this.getAttribute('data-deferred-menu-section-id');
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error('Deferred menu section request failed');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceDrawer = doc.querySelector('header-drawer.header-drawer--mobile');
      const sourceContainer = sourceDrawer?.querySelector('.menu-drawer__container');

      if (!sourceContainer) throw new Error('Deferred menu source container missing');

      const sourceTemplate = sourceContainer.querySelector('template[data-deferred-menu-template]');
      container.textContent = '';

      if (sourceTemplate instanceof HTMLTemplateElement) {
        container.append(sourceTemplate.content.cloneNode(true));
      } else {
        container.innerHTML = sourceContainer.innerHTML;
      }

      container.dataset.deferredMenuLoaded = 'true';
      this.#setupAnimatedElementListeners();
    } catch (error) {
      console.warn('[header-drawer] Deferred menu failed:', error);
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

   async #initSwipers(scope) {
    const hasSwiperContent = scope.querySelector('[data-swiper="banner-carousel"], [data-swiper="trending-carousel"]');
    if (!hasSwiperContent) return;

    await this.#ensureSwiperLoaded();
    if (typeof window.Swiper === 'undefined') return;

    // Banner carousel (1 slide per view)
    this.#initSwiperByType(scope, 'banner-carousel', {
      slidesPerView: 1,
      loop: true,
      speed: 400,
      autoplay: { delay: 3500, disableOnInteraction: false },
      pagination: { clickable: true }
    });

    // Trending carousel (auto width chips)
    this.#initSwiperByType(scope, 'trending-carousel', {
      slidesPerView: 'auto',
      spaceBetween: 10,
      freeMode: true,
      loop: false,
      speed: 300
    });
  }

  #initSwiperByType(scope, type, options) {
    const roots = scope.querySelectorAll(`[data-swiper="${type}"]`);

    roots.forEach((root) => {
      if (root.dataset.swiperInited === '1') return;

      const el = root.querySelector('.swiper');
      if (!el) return;

      // Skip Swiper entirely for single-slide carousels: a lone slide never
      // needs touch/swipe/autoplay/pagination logic, and the wrapper's own
      // layout (flex/block) already displays it correctly with zero JS.
      const slideCount = el.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length;
      if (slideCount <= 1) {
        root.dataset.swiperInited = '1';
        el.classList.add('swiper--static');
        return;
      }

      const paginationEl = root.querySelector('.swiper-pagination');

      root.dataset.swiperInited = '1';

      const config = { ...options };

      if (config.pagination) {
        if (paginationEl) config.pagination = { ...config.pagination, el: paginationEl };
        else delete config.pagination;
      }

      // eslint-disable-next-line no-new
      new window.Swiper(el, config);
    });
  }

  #ensureSwiperLoaded() {
    if (typeof window.Swiper !== 'undefined') return Promise.resolve();
    if (window.__shoplcSwiperLoading) return window.__shoplcSwiperLoading;

    const assets = window.ShopLCSwiperAssets || {};

    if (assets.css && !document.querySelector(`link[href="${assets.css}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = assets.css;
      document.head.append(link);
    }

    window.__shoplcSwiperLoading = new Promise((resolve) => {
      if (!assets.js) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src="${assets.js}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => resolve(), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = assets.js;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.append(script);
    });

    return window.__shoplcSwiperLoading;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}